import React, { useEffect, useRef, useState } from "react";
import { api, API, formatApiError } from "../lib/api";
import { useBranding } from "../components/Layout";
import { FolderArchive, Download, FileText, Upload as UploadIcon, Trash2 } from "lucide-react";

// Documents your listing agent uploads for you (read-only on this page).
const DOC_LABELS = {
  listing_agreement: "Listing Agreement",
  disclosures: "Seller Disclosures",
  inspection: "Inspection Report",
  appraisal: "Appraisal",
  contract: "Purchase Contract",
  addendum: "Addendum",
  closing: "Closing Documents",
  other: "Other",
};

// HOA categories — sellers upload these themselves. Order here drives
// display order. Each section accepts multiple files.
const HOA_CATEGORIES = [
  { id: "hoa_ccrs", label: "CC&Rs", hint: "Covenants, Conditions & Restrictions" },
  { id: "hoa_rules", label: "Rules & Regulations", hint: "Architectural guidelines, pet rules, etc." },
  { id: "hoa_budget", label: "Budget & Financials", hint: "Annual budget, reserve study, audit" },
  { id: "hoa_meetings", label: "Recent Meetings", hint: "Board / annual meeting minutes" },
];
const HOA_CATEGORY_IDS = HOA_CATEGORIES.map((c) => c.id);

function fmtBytes(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function Documents() {
  const branding = useBranding();
  const [docs, setDocs] = useState([]);
  const [hasHoa, setHasHoa] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const reloadDocs = async () => {
    try {
      const { data } = await api.get("/me/documents");
      setDocs(data.documents || []);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Could not load documents");
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [docsRes, perRes] = await Promise.all([
          api.get("/me/documents"),
          api.get("/me/personalization").catch(() => ({ data: { personalization: {} } })),
        ]);
        if (!alive) return;
        setDocs(docsRes.data.documents || []);
        setHasHoa(!!perRes.data?.personalization?.has_hoa);
      } catch (e) {
        if (alive) setErr(formatApiError(e?.response?.data?.detail) || "Could not load documents");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Group docs once, then split into agent-uploaded vs HOA buckets so
  // each section only renders what belongs to it.
  const grouped = docs.reduce((acc, d) => {
    const k = d.category || "other";
    (acc[k] = acc[k] || []).push(d);
    return acc;
  }, {});
  const agentDocKeys = Object.keys(DOC_LABELS).filter((k) => grouped[k]?.length);

  // Most recent upload (docs are returned sorted desc, but recompute to be safe)
  const latestUploadedAt = docs.reduce((max, d) => {
    const t = d.uploaded_at ? new Date(d.uploaded_at).getTime() : 0;
    return t > max ? t : max;
  }, 0);

  const relativeTime = (ts) => {
    if (!ts) return "";
    const diff = Math.max(0, Date.now() - ts);
    const mins = Math.round(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
    const days = Math.round(hours / 24);
    if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
    return new Date(ts).toLocaleDateString();
  };

  return (
    <div className="container-x" style={{ padding: "48px 0 64px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <FolderArchive size={20} style={{ color: "var(--primary)" }} />
        <span className="overline">Your documents</span>
      </div>
      <h1 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 500, margin: "8px 0 12px", letterSpacing: "-0.01em" }}>
        Everything in one place.
      </h1>
      <p style={{ color: "var(--text-muted)", maxWidth: 720, fontSize: "1.05rem" }}>
        Anything {branding.agent_first_name} has uploaded for you — disclosures, contracts, inspection reports, closing docs — lives here. Download any time.
      </p>
      {latestUploadedAt > 0 && (
        <p data-testid="documents-last-updated" style={{ color: "var(--text-muted)", fontSize: 13, fontStyle: "italic", marginTop: 8 }}>
          {branding.agent_first_name} last uploaded a doc {relativeTime(latestUploadedAt)}.
        </p>
      )}

      {loading && <div style={{ color: "var(--text-muted)", marginTop: 32 }}>Loading…</div>}
      {err && <div data-testid="documents-error" style={{ marginTop: 24, padding: "12px 16px", borderRadius: 12, background: "#F7E2E2", color: "var(--error)", fontSize: 14 }}>{err}</div>}

      {!loading && !err && agentDocKeys.length === 0 && !hasHoa && (
        <div className="card" data-testid="documents-empty" style={{ padding: 40, marginTop: 28, textAlign: "center", color: "var(--text-muted)" }}>
          <FileText size={28} style={{ color: "var(--text-muted)", margin: "0 auto 12px" }} />
          <div>No documents yet. {branding.agent_first_name} will upload signed paperwork here as you move through the process.</div>
        </div>
      )}

      {agentDocKeys.map((key) => (
        <section key={key} style={{ marginTop: 32 }} data-testid={`docs-section-${key}`}>
          <div className="overline" style={{ marginBottom: 12 }}>{DOC_LABELS[key]}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
            {grouped[key].map((d) => (
              <DocCard key={d.doc_id} doc={d} />
            ))}
          </div>
        </section>
      ))}

      {hasHoa && (
        <HoaPacket
          docsByCategory={grouped}
          onChange={reloadDocs}
          agentFirstName={branding.agent_first_name}
        />
      )}
    </div>
  );
}

function DocCard({ doc }) {
  return (
    <div className="card" data-testid={`doc-card-${doc.doc_id}`} style={{ padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 500, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.filename}</div>
          <div style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 6 }}>
            {fmtBytes(doc.size)} · {new Date(doc.uploaded_at).toLocaleDateString()}
          </div>
          {doc.notes && <div style={{ fontSize: 13, marginTop: 8, color: "var(--text)" }}>{doc.notes}</div>}
        </div>
        <a
          data-testid={`doc-download-${doc.doc_id}`}
          className="btn-primary"
          style={{ padding: "8px 14px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}
          href={`${API}/documents/${doc.doc_id}/download`}
          target="_blank"
          rel="noreferrer"
        >
          <Download size={13} /> Download
        </a>
      </div>
    </div>
  );
}

/** HOA packet block: 4 sections (CC&Rs / Rules / Budget / Meetings).
 *  Each section accepts multiple file uploads. Sellers can delete their
 *  own uploads via the trash icon. Only renders if `has_hoa` is true.
 */
function HoaPacket({ docsByCategory, onChange, agentFirstName }) {
  return (
    <section style={{ marginTop: 56 }} data-testid="hoa-packet">
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 6 }}>
        <div>
          <span className="overline">HOA packet</span>
          <h2 className="font-serif" style={{ fontSize: "clamp(1.6rem, 2.6vw, 2rem)", fontWeight: 500, margin: "6px 0 6px", letterSpacing: "-0.005em" }}>
            Upload your HOA documents
          </h2>
          <p style={{ color: "var(--text-muted)", maxWidth: 680, fontSize: 14, lineHeight: 1.6, margin: 0 }}>
            Buyers and their agents will ask for these — adding them here means {agentFirstName} can hand them over the moment an offer comes in. PDFs or images, max 25 MB each. Upload as many as you have per section.
          </p>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16, marginTop: 22 }}>
        {HOA_CATEGORIES.map((cat) => (
          <HoaSection
            key={cat.id}
            category={cat}
            files={docsByCategory[cat.id] || []}
            onChange={onChange}
          />
        ))}
      </div>
    </section>
  );
}

function HoaSection({ category, files, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const upload = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (list.length === 0) return;
    for (const file of list) {
      if (file.size > 25 * 1024 * 1024) {
        setErr(`"${file.name}" is over 25 MB. Try a smaller file.`);
        continue;
      }
      setBusy(true);
      setErr("");
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("category", category.id);
        await api.post("/me/hoa-docs", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch (ex) {
        setErr(formatApiError(ex?.response?.data?.detail) || `Couldn't upload "${file.name}".`);
        break;
      } finally {
        setBusy(false);
      }
    }
    onChange?.();
  };

  const removeFile = async (doc_id, filename) => {
    if (!window.confirm(`Remove "${filename}"?`)) return;
    try {
      await api.delete(`/me/hoa-docs/${doc_id}`);
      onChange?.();
    } catch (ex) {
      setErr(formatApiError(ex?.response?.data?.detail) || "Couldn't remove file.");
    }
  };

  return (
    <div
      className="card"
      data-testid={`hoa-section-${category.id}`}
      style={{ padding: 22, display: "flex", flexDirection: "column", gap: 12 }}
    >
      <div>
        <div className="overline">{category.label}</div>
        <p style={{ color: "var(--text-muted)", fontSize: 13, margin: "4px 0 0" }}>{category.hint}</p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {files.length === 0 && (
          <div data-testid={`hoa-empty-${category.id}`} style={{ fontSize: 13, color: "var(--text-faint)", fontStyle: "italic" }}>
            No files yet.
          </div>
        )}
        {files.map((d) => (
          <div
            key={d.doc_id}
            data-testid={`hoa-file-${d.doc_id}`}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "8px 10px", border: "1px solid var(--border)",
              borderRadius: 8, background: "var(--surface-alt)",
            }}
          >
            <FileText size={14} style={{ flexShrink: 0, color: "var(--text-muted)" }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.filename}</div>
              <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                {fmtBytes(d.size)} · {new Date(d.uploaded_at).toLocaleDateString()}
              </div>
            </div>
            <a
              data-testid={`hoa-download-${d.doc_id}`}
              href={`${API}/documents/${d.doc_id}/download`}
              target="_blank"
              rel="noreferrer"
              style={{ color: "var(--primary)", display: "inline-flex", alignItems: "center" }}
              title="Download"
            >
              <Download size={14} />
            </a>
            {/* Only the uploader (seller) can delete via this endpoint —
                if backend says no, we surface the error. */}
            <button
              type="button"
              data-testid={`hoa-delete-${d.doc_id}`}
              onClick={() => removeFile(d.doc_id, d.filename)}
              style={{ background: "transparent", border: "none", color: "var(--text-muted)", cursor: "pointer", display: "inline-flex", alignItems: "center" }}
              title="Remove"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>

      <input
        ref={inputRef}
        data-testid={`hoa-upload-input-${category.id}`}
        type="file"
        multiple
        onChange={upload}
        style={{ display: "none" }}
        accept=".pdf,.png,.jpg,.jpeg,.heic,.doc,.docx,.txt"
      />
      <button
        type="button"
        data-testid={`hoa-upload-btn-${category.id}`}
        className="btn-secondary"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        style={{ alignSelf: "flex-start", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}
      >
        <UploadIcon size={13} /> {busy ? "Uploading…" : files.length > 0 ? "Add another" : "Upload file"}
      </button>
      {err && (
        <div data-testid={`hoa-err-${category.id}`} style={{ fontSize: 12, color: "var(--error)" }}>{err}</div>
      )}
    </div>
  );
}

// Exported so the Dashboard's reminder card can use the same labels/ids.
export { HOA_CATEGORIES, HOA_CATEGORY_IDS };
